import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { skipWhile, take, withLatestFrom } from 'rxjs/operators';
import Utils from 'src/app/utils';

import { BREEDS } from '../../models/breeds';
import { Breed, Order, PropagationCompany } from '../../models/cycle.model';
import { setSelectedOrderId } from '../../store/actions/cycle.actions';
import { getPropagationCompanies, getSelectedOrder } from '../../store/selectors/cycle.selectors';

export interface OrderItemDialogInputData extends AbstractFormDialogData {
  orderId?: number;
}
export interface OrderItemDialogResultData {
  order: Order;
}

@Component({
  selector: 'app-order-item-dialog',
  templateUrl: './order-item-dialog.component.html',
  styleUrls: ['./order-item-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AgrNumberDirective,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class OrderItemDialogComponent extends AbstractDialog<OrderItemDialogResultData> {
  id: number;

  readonly formGroup = new FormGroup({
    propagationCompany: new FormControl<PropagationCompany>(undefined, [Validators.required]),
    expectedDeliveryDate: new FormControl<string>(''),
    breed: new FormControl<Breed>(undefined, Validators.required),
    numberOfFemales: new FormControl<number>(undefined, []),
    numberOfMales: new FormControl<number>(undefined, []),
    isFulFilled: new FormControl(false, []),
  });

  readonly breeds: Breed[] = BREEDS;
  readonly propagationCompanies$ = this.store.select(getPropagationCompanies);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrderItemDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      OrderItemDialogComponent,
      AbstractDialogResultData<OrderItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {
    super();
    this.id = this.data.orderId;

    if (this.id > 0) {
      this.data.title = this.localeService.translate('order.edit-order');

      this.store.dispatch(setSelectedOrderId({ id: this.id }));
      this.store
        .select(getSelectedOrder)
        .pipe(
          withLatestFrom(this.propagationCompanies$),
          skipWhile(
            ([order, propagationCompanies]) =>
              order === null || order === undefined || propagationCompanies?.length === 0,
          ),
          take(1),
        )
        .subscribe(([order, propagationCompanies]) => {
          this.formGroup.controls.propagationCompany.setValue(
            propagationCompanies.find((p) => p.id === order.propagationCompany.id),
          );
          this.formGroup.controls.expectedDeliveryDate.setValue(order.arrivalDate);
          this.formGroup.controls.breed.setValue(this.breeds.find((b) => b.id === order.breed.id));
          this.formGroup.controls.numberOfFemales.setValue(order.numberOfFemales);
          this.formGroup.controls.numberOfMales.setValue(order.numberOfMales);
          this.formGroup.controls.isFulFilled.setValue(order.isFulFilled);
        });
    } else {
      this.data.title = this.localeService.translate('order.add-order');
    }
  }

  save() {
    if (this.formGroup.valid) {
      const order: Order = {
        id: this.id,
        propagationCompany: this.formGroup.controls.propagationCompany.value,
        arrivalDate:
          typeof (<unknown>this.formGroup.controls.expectedDeliveryDate.value) !== 'string'
            ? Utils.toUtcDate(
                new Date(this.formGroup.controls.expectedDeliveryDate.value),
              ).toISOString()
            : this.formGroup.controls.expectedDeliveryDate.value,
        breed: this.formGroup.controls.breed.value,
        numberOfFemales: this.formGroup.controls.numberOfFemales.value,
        numberOfMales: this.formGroup.controls.numberOfMales.value,
        isFulFilled: this.formGroup.controls.isFulFilled.value,
      };
      this.confirm({
        order,
      });
    }
  }
}
